import { useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BackstageTheme } from '@backstage/theme';
import { Link } from '@backstage/core-components';

// Arrive brand colours (P1 purple, P2 pink).
const ARRIVE_PURPLE = '#5F016F';
const ARRIVE_PINK = '#FF33BB';
// Warm amber for the STATUS column — the classic departure-board status colour.
const FLAP_AMBER = '#FFC65A';
const FLAP_WHITE = '#F2EEF5';
const BOARD_BG = '#1c0f24';
const CELL_BG = '#120a18';

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:-? ';
const MONO = "'Roboto Mono', 'DejaVu Sans Mono', 'Courier New', monospace";

// Train departure board columns. Each value is padded to its width so the
// whole row is one continuous, fixed-width flap line (spaces included).
const COLS = [
  { title: 'TRAIN', key: 'train', width: 8 },
  { title: 'DESTINATION', key: 'dest', width: 16 },
  { title: 'TIME', key: 'time', width: 6 },
  { title: 'PLAT', key: 'plat', width: 5 },
  { title: 'STATUS', key: 'status', width: 10 },
] as const;

const LINE_LEN = COLS.reduce((sum, c) => sum + c.width, 0);
// Index where the STATUS column begins → those cells flap amber, the rest white.
const STATUS_FROM = LINE_LEN - COLS[COLS.length - 1].width;

const pad = (value: string, width: number) =>
  value.toUpperCase().padEnd(width, ' ').slice(0, width);

const titleLine = COLS.map(c => pad(c.title, c.width)).join('');

type RowData = Record<(typeof COLS)[number]['key'], string> & {
  // When set, the whole row becomes a link to this route (the way out).
  to?: string;
};
const buildLine = (row: RowData) =>
  COLS.map(c => pad(row[c.key] ?? '', c.width)).join('');

// The 404 told as a train board: the page you wanted is delayed (headcode
// 404), and the 200 service to Home is arriving — that row links to the
// homepage.
const ROWS: RowData[] = [
  {
    train: '404',
    dest: 'NOTFOUNDVILLE',
    time: '--:--',
    plat: '0',
    status: 'DELAYED',
  },
  {
    train: '200',
    dest: 'HOME',
    time: 'NOW',
    plat: '1',
    status: 'ARRIVING',
    to: '/home',
  },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useStyles = makeStyles<BackstageTheme>(theme => ({
  root: {
    position: 'relative',
    minHeight: '100vh',
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  board: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '96vw',
    maxWidth: 2200,
    minHeight: '75vh',
    borderRadius: 18,
    backgroundColor: BOARD_BG,
    boxShadow: '0 28px 70px rgba(0, 0, 0, 0.55)',
    border: `1px solid ${ARRIVE_PURPLE}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: { width: '94vw', minHeight: 0 },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2.5),
    background: ARRIVE_PURPLE,
  },
  liveDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: ARRIVE_PINK,
    boxShadow: `0 0 10px ${ARRIVE_PINK}`,
    animation: '$pulse 1.6s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.25 },
  },
  clock: {
    fontFamily: MONO,
    fontSize: 'clamp(16px, 1.8vw, 26px)',
    letterSpacing: 2,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: '#fff',
  },
  body: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1) },
  },
  titleRow: {
    display: 'flex',
    gap: 'clamp(1px, 0.2vw, 3px)',
    padding: theme.spacing(0, 0, 1),
  },
  titleCell: {
    flex: '1 1 0',
    minWidth: 0,
    textAlign: 'center',
    fontFamily: MONO,
    fontSize: 'clamp(8px, 1.3vw, 18px)',
    letterSpacing: 0,
    color: 'rgba(255, 255, 255, 0.4)',
  },
  flapRow: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'stretch',
    gap: 'clamp(1px, 0.2vw, 3px)',
    padding: theme.spacing(0.75, 0),
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  },
  flapRowLink: {
    cursor: 'pointer',
    textDecoration: 'none',
    borderRadius: 'clamp(3px, 0.4vw, 7px)',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    outline: 'none',
    // Lift + brand glow on hover/focus so the row reads as the action.
    '&:hover, &:focus-visible': {
      transform: 'scale(1.012)',
      boxShadow: `0 0 0 2px ${ARRIVE_PINK}, 0 10px 28px rgba(255, 51, 187, 0.3)`,
    },
    '&:hover $cell, &:focus-visible $cell': {
      color: ARRIVE_PINK,
    },
    '&:hover $cellAccent, &:focus-visible $cellAccent': {
      color: ARRIVE_PINK,
    },
  },
  cell: {
    position: 'relative',
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'clamp(3px, 0.4vw, 7px)',
    background: `linear-gradient(180deg, #2a1830 0%, ${CELL_BG} 50%, #0c0710 50%, #1a1020 100%)`,
    color: FLAP_WHITE,
    fontFamily: MONO,
    fontSize: 'clamp(11px, 2.9vw, 62px)',
    fontWeight: 700,
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.6)',
    // centre seam, like a real split-flap card
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: 1,
      background: 'rgba(0, 0, 0, 0.55)',
    },
  },
  cellAccent: {
    color: FLAP_AMBER,
  },
  footer: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: `1px solid ${ARRIVE_PURPLE}`,
    background: 'rgba(0, 0, 0, 0.25)',
    fontFamily: MONO,
    fontSize: 'clamp(11px, 1.4vw, 18px)',
    letterSpacing: 1,
    color: 'rgba(255, 255, 255, 0.55)',
    textAlign: 'center',
  },
  footerMark: { color: ARRIVE_PINK, margin: theme.spacing(0, 1) },
}));

/**
 * One full-width split-flap board line. Every character — including the
 * spaces that separate columns — is its own flap module, so the whole line
 * is used. On mount (after `delay`) and periodically the characters scramble
 * through random glyphs and settle left-to-right. Cells from `accentFrom`
 * onward flap amber (the STATUS column); the rest are white. Respects
 * prefers-reduced-motion by showing the settled line immediately.
 */
const FlapLine = ({
  text,
  accentFrom = Infinity,
  delay = 0,
  to,
  ariaLabel,
}: {
  text: string;
  accentFrom?: number;
  delay?: number;
  to?: string;
  ariaLabel?: string;
}) => {
  const classes = useStyles();
  const len = text.length;
  const [chars, setChars] = useState<string[]>(() =>
    prefersReducedMotion() ? text.split('') : Array(len).fill(' '),
  );
  const raf = useRef<number>();

  useEffect(() => {
    if (prefersReducedMotion()) {
      setChars(text.split(''));
      return undefined;
    }

    const run = () => {
      const start = performance.now();
      let last = 0;
      // Each column settles a little after the previous one — a left-to-right
      // ripple across the line.
      const settleAt = text.split('').map((_, i) => 400 + i * 40);

      const tick = (now: number) => {
        const elapsed = now - start;
        if (now - last >= 50) {
          last = now;
          setChars(
            text
              .split('')
              .map((char, i) =>
                elapsed >= settleAt[i]
                  ? char
                  : GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
              ),
          );
        }
        if (elapsed < settleAt[len - 1]) {
          raf.current = requestAnimationFrame(tick);
        } else {
          setChars(text.split(''));
        }
      };
      raf.current = requestAnimationFrame(tick);
    };

    let interval: ReturnType<typeof setInterval> | undefined;
    const startTimer = setTimeout(() => {
      run();
      interval = setInterval(run, 12000);
    }, delay);

    return () => {
      clearTimeout(startTimer);
      // Explicit undefined checks: a timer/raf id of 0 is a valid value in
      // some environments and must still be cleared.
      if (interval !== undefined) {
        clearInterval(interval);
      }
      if (raf.current !== undefined) {
        cancelAnimationFrame(raf.current);
      }
    };
  }, [text, len, delay]);

  // Individual flap cells are decorative; the row exposes a single readable
  // label to assistive tech (so screen readers don't spell it out per glyph).
  const cells = chars.map((c, i) => (
    <span
      // eslint-disable-next-line react/no-array-index-key
      key={i}
      aria-hidden="true"
      className={`${classes.cell} ${i >= accentFrom ? classes.cellAccent : ''}`}
    >
      {c === ' ' ? ' ' : c}
    </span>
  ));

  // A linked row is the page's primary action (the "boarding" service).
  if (to) {
    return (
      <Link
        to={to}
        className={`${classes.flapRow} ${classes.flapRowLink}`}
        aria-label={ariaLabel}
      >
        {cells}
      </Link>
    );
  }

  // A non-interactive row reads as a single image with its text alternative.
  return (
    <div className={classes.flapRow} role="img" aria-label={ariaLabel}>
      {cells}
    </div>
  );
};

// Explicit 24h HH:MM:SS — toLocaleTimeString defaults vary by locale.
const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};
const nowHms = () => new Date().toLocaleTimeString([], TIME_FORMAT);

/** Live HH:MM:SS clock for the board header — quiet "you're waiting" detail. */
const Clock = ({ className }: { className?: string }) => {
  const [time, setTime] = useState(nowHms);
  useEffect(() => {
    const id = setInterval(() => setTime(nowHms()), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className={className}>{time}</span>;
};

/**
 * Custom full-page 404 shown for unmatched routes. Registered as the
 * `NotFoundErrorPage` component override in `App.tsx` (createApp).
 *
 * The page is a single Arrive-branded train departure board rendered as a
 * real split-flap display: TRAIN · DESTINATION · TIME · PLAT · STATUS, every
 * character its own flap module. The 404 is told as train headcode 404
 * (delayed / not found) with the 200 service boarding for the homepage. The
 * board is intentionally dark (as real boards are) and reads on both themes.
 */
export const NotFoundPage = () => {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <div
        className={classes.board}
        role="region"
        aria-label="Train departure board — error 404"
      >
        <div className={classes.header}>
          <span className={classes.liveDot} />
          <Clock className={classes.clock} />
        </div>

        <div className={classes.body}>
          <div className={classes.titleRow} aria-hidden="true">
            {titleLine.split('').map((c, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className={classes.titleCell}>
                {c === ' ' ? ' ' : c}
              </span>
            ))}
          </div>

          {ROWS.map((row, i) => (
            <FlapLine
              key={row.train + row.dest}
              text={buildLine(row)}
              accentFrom={STATUS_FROM}
              delay={i * 350}
              to={row.to}
              ariaLabel={
                row.to
                  ? `Board the ${row.dest} service — go to homepage`
                  : `Train ${row.train} to ${row.dest}: ${row.status}`
              }
            />
          ))}
        </div>

        <div className={classes.footer}>
          <span className={classes.footerMark}>●</span>
          It seems that this page hasn't arrived yet — board the homepage
          instead
          <span className={classes.footerMark}>●</span>
        </div>
      </div>
    </Box>
  );
};
